import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { filter, map, finalize, mergeMap, take, tap } from 'rxjs/operators';
import { MatLegacyDialogRef as MatDialogRef, MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import moment from 'moment';

import { RegisterAccountViewModel } from '../../core/models/view-models/account-models/user-models/register-account-view-model.type';
import { DisplayAccountViewModel } from '../../core/models/view-models/account-models/user-models/display-account-view-model.type';
import { UpdateAccountViewModel } from '../../core/models/view-models/account-models/user-models/update-account-view-model';
import { PlaylistDTO } from '../../core/models/view-models/playlist-models/playlist-dto.model';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { PlaylistsByUser } from '../../core/models/playlistByUser';
import { Monitor } from '../../core/models/entities/monitor.type';
import { Machine } from '../../core/models/entities/machine.type';
import { Role } from '../../core/models/entities/role.type';
import { AppConstants } from '../../app.constants';
import { MonitorTagByUser } from '../../core/models/view-models/tag-models/tag-by-user.view-model';
import { TagViewModel } from '../../core/models/view-models/tag-models/tag-view.model';
import { ChangePasswordViewModel } from '../../core/models/view-models/account-models/change-password-view-model.type';
import { ModalTwoFactorAuthenticationComponent } from '../../shared-module/components/_modals/modal-two-factor-authentication/modal-two-factor-authentication.component';
import { ToastrMessageEnum } from '../../core/enums/toastr-messages/toastr-message.enum';
import { MachineCategory } from '../../core/models/entities/machine-category.type';
import { ModalAddUserApiTokenComponent } from '../../shared-module/components/_modals/modal-add-user-api-token/modal-add-user-api-token.component';
import { ApiToken } from '../../core/models/view-models/account-models/user-models/api-token.model';
import { CategoryFilterPipe } from '../../core/pipes/category-filter.pipe';
import { ModalLegalConsentComponent } from '../../shared-module/components/_modals/modal-legal-consent/modal-legal-consent.component';
import { LegalConsentModel } from '../../core/models/view-models/account-models/legal-consent.model';

import { AdministratorPlaylistService } from '../../core/services/http-services/administrator.playlist.service';
import { AdministrationService } from '../../core/services/http-services/administrator.service';
import { UtilsService } from '../../core/services/http-services/utils.service';
import { AdministratorMonitorTagService } from '../../core/services/http-services/administrator.monitor-tag.service';
import { AuthService } from '../../core/services/auth/auth.service';
import { DeleteDialogService } from '../../core/services/dialog-services/delete-dialog.service';
import { MachineCategoriesService } from '../../core/services/http-services/machine-categories.service';
import { MonitorsService } from '../../core/services/http-services/monitors.service';

@Component({
  selector: 'modal-window-users',
  templateUrl: './modal-edit-user.component.html',
  styleUrls: ['./modal-edit-user.component.scss'],
  providers: [CategoryFilterPipe]
})
export class ModalWidowUsersComponent implements OnInit, OnDestroy {
  @Input() user: DisplayAccountViewModel;
  @Input() allowEditAdminRole: boolean;
  @Input() isAdmin: boolean;
  @ViewChild('form') ngForm: NgForm;
  @ViewChild('changePasswordForm') changePasswordForm: NgForm;

  private currentUserRoles: string[] = [];
  private subscriptions: Subscription = new Subscription();

  languages: any[] = [];
  roles: Role[] = [];
  playlistsByUsersCollection: PlaylistsByUser[];
  tagsByUsersCollection: MonitorTagByUser[];
  filteredCategories: MachineCategory[] = [];
  categories: MachineCategory[] = [];
  categories$: Observable<MachineCategory[]>;
  filterCategoryId: number = null;
  adminRole: string = AppConstants.ADMINISTRATOR_ROLE;
  editorRole: string = AppConstants.EDITOR_ROLE;
  categoryManagerRole: string = AppConstants.CATEGORY_MANAGER_ROLE;
  passwordPattern: RegExp = AppConstants.PASSWORD_REGULAR;
  uncategorizedColor = AppConstants.MACHINE_UNCATEGORIZED_BACKGROUND_COLOR;
  userApiTokens: ApiToken[];
  dataSource: MatTableDataSource<ApiToken>;
  displayedColumns = ['Name', 'DateRegistered', 'actions'];
  isLegalConsentOptionAllowed: boolean;
  saveInProgress: boolean;

  changePasswordModel = new ChangePasswordViewModel();

  userSharedPlaylistsIds: number[] = [];
  userSharedMonitorTagsIds: number[] = [];
  availableScreenKeys: MonitorKey[] = [];
  availableMachines: string[] = [];
  availableCategoriesIds: number[] = [];
  machines: Machine[] = [];

  passwordModel: string;
  confirmPasswordModel: string;
  filterText: string;
  isPasswordExpired: boolean;

  hidePass = true;
  hideOldPass = true;
  hideConfirm = true;
  hideCurrent = true;

  constructor(
    private readonly dialogRef: MatDialogRef<ModalWidowUsersComponent>,
    private readonly utilsService: UtilsService,
    private readonly toastrService: ToastrService,
    private readonly adminService: AdministrationService,
    private readonly admPlaylistsDataService: AdministratorPlaylistService,
    private readonly admMonitorTagsDataService: AdministratorMonitorTagService,
    private readonly categoryFilterPipe: CategoryFilterPipe,
    private readonly authService: AuthService,
    private readonly deleteDialogService: DeleteDialogService,
    private readonly dialog: MatDialog,
    private readonly categoryService: MachineCategoriesService,
    private readonly monitorsService: MonitorsService
  ) { }

  ngOnInit(): void {
    this.fetchDataByUser();

    if (!this.user) {
      this.user = new DisplayAccountViewModel();
      this.user.Roles = [AppConstants.EDITOR_ROLE];

      this.authService.getLegalConsent().pipe(take(1)).subscribe((data) => {
        this.isLegalConsentOptionAllowed = !!data;
      });
    } else {
      this.availableCategoriesIds = this.user.AvailableCategories.map(category => category.Id);
      this.userSharedPlaylistsIds = this.user.SharedPlaylists.map(playlist => playlist.Id);
      this.userSharedMonitorTagsIds = this.user.SharedMonitorTags.map(tag => tag.Id);

      this.authService.getUserApiToken(this.user.Id)
        .pipe(
          take(1)
        )
        .subscribe(data => {
          this.userApiTokens = data;
          this.dataSource = new MatTableDataSource(this.userApiTokens);
        });
    }

    this.currentUserRoles = _.cloneDeep(this.user.Roles);

    this.isPasswordExpired = this.user.ShouldPasswordExpire &&
      moment().diff(moment(new Date(this.user.LastPasswordUpdateDate)), 'days') >= AppConstants.DEFAULT_PASSWORD_EXPIRATION_DAYS;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  close(): void {
    this.dialogRef.close();
  }

  onRoleChanged(checked: boolean, role: Role) {
    this.ngForm.form.markAsDirty();

    if (checked && !this.user.Roles.includes(role.Name)) {
      this.user.Roles.push(role.Name);

      if (role.Name === this.editorRole || role.Name === this.adminRole) {
        this.user.HasAccessToRemoteControl = true;
      }
    } else {
      const i = this.user.Roles.indexOf(role.Name);
      if (i >= 0) {
        this.user.Roles.splice(i, 1);
      }
    }
  }

  onSelectAllLocations(checkedAll: boolean): void {
    this.ngForm.form.markAsDirty();

    if (checkedAll) {
      this.selectAllLocations(this.filteredCategories);
    } else {
      const filteredLocationAddresses = this.filteredCategories.flatMap(category =>
        category.Machines.map(location =>
          location.PhysicalAddress));

      this.availableCategoriesIds = this.availableCategoriesIds.filter(categoryId =>
        !this.filteredCategories.some(category =>
          category.Id === categoryId));

      this.availableMachines = this.availableMachines.filter(machine => !filteredLocationAddresses.includes(machine));

      this.filteredCategories.forEach(category =>
        category.Machines.forEach(location => {
          location.Monitors?.forEach(monitor => {
            _.remove(this.availableScreenKeys, (x: MonitorKey) => {
              return (
                x.MonitorHandler == monitor.MonitorHandler &&
                x.MachinePhysicalAddress == monitor.MachinePhysicalAddress
              );
            });
          })
        })
      );
    }
  }

  onCategoryCheckChanged(checked: boolean, category: MachineCategory): void {
    this.ngForm.form.markAsDirty();
    const physicalAdresses = category.Machines.map(category => category.PhysicalAddress);

    if (checked) {

      if (category.Id) {
        this.availableCategoriesIds.push(category.Id);
      }

      this.availableMachines.push(...physicalAdresses);
      category.Machines.forEach(m => m.Monitors?.forEach(monitor => {
        this.availableScreenKeys.push(
          new MonitorKey(monitor.MonitorHandler, monitor.MachinePhysicalAddress)
        );
      }));
    } else {
      this.availableCategoriesIds = this.availableCategoriesIds.filter(categoryId => categoryId !== category.Id);
      this.availableMachines = this.availableMachines.filter(m => !physicalAdresses.includes(m));
      category.Machines.forEach(m =>
        this.availableScreenKeys = this.availableScreenKeys.filter(screenKey =>
          screenKey.MachinePhysicalAddress !== m.PhysicalAddress
        )
      );
    }
  }

  onMachineCheckChanged(mustBeInCollection: boolean, location: Machine, category: MachineCategory) {
    this.ngForm.form.markAsDirty();
    if (mustBeInCollection) {

      if (location.CategoryId) {
        this.availableCategoriesIds.push(location.CategoryId);
      }

      this.availableMachines.push(location.PhysicalAddress);
      location.Monitors?.forEach(monitor => {
        this.availableScreenKeys.push(
          new MonitorKey(monitor.MonitorHandler, monitor.MachinePhysicalAddress)
        );
      });
    } else {
      _.remove(
        this.availableMachines,
        machine => machine == location.PhysicalAddress
      );
      location.Monitors?.forEach(monitor => {
        _.remove(this.availableScreenKeys, (x: MonitorKey) => {
          return (
            x.MonitorHandler == monitor.MonitorHandler &&
            x.MachinePhysicalAddress == monitor.MachinePhysicalAddress
          );
        });
      });

      if (!category.Machines.some(m => this.availableMachines.includes(m.PhysicalAddress))) {
        this.availableCategoriesIds = this.availableCategoriesIds.filter(categoryId => categoryId !== category.Id);
      }
    }
  }

  onMonitorCheckChanged(checked: boolean, monitor: Monitor, category: MachineCategory) {
    this.ngForm.form.markAsDirty();
    if (checked) {
      this.availableScreenKeys.push(
        new MonitorKey(monitor.MonitorHandler, monitor.MachinePhysicalAddress)
      );
      this.updateAvailableMachines(this.machines);
    } else {
      _.remove(this.availableScreenKeys, (x: MonitorKey) => {
        return (
          x.MonitorHandler == monitor.MonitorHandler &&
          x.MachinePhysicalAddress == monitor.MachinePhysicalAddress
        );
      });
      _.remove(this.availableMachines, machine => {
        return machine === monitor.MachinePhysicalAddress;
      });

      if (!category.Machines.some(m => this.availableMachines.includes(m.PhysicalAddress))) {
        this.availableCategoriesIds = this.availableCategoriesIds.filter(categoryId => categoryId !== category.Id);
      }
    }
  }

  isPlaylistCheckboxDisabled(playlist: PlaylistDTO) {
    return playlist.MachinePhysicalAddress ?
      !this.availableScreenKeys.some(screenKey => screenKey.MachinePhysicalAddress.includes(playlist.MachinePhysicalAddress)) :
      false;
  }

  onPlaylistItemChanged(
    mustBeInCollection: boolean,
    playlist: PlaylistDTO,
    user?: PlaylistsByUser
  ): void {
    this.ngForm.form.markAsDirty();

    if (mustBeInCollection) {
      if (!this.userSharedPlaylistsIds.includes(playlist.Id)) {
        this.userSharedPlaylistsIds.push(playlist.Id);
      }
    } else {
      _.remove(this.userSharedPlaylistsIds, (x: number) => x === playlist.Id);
    }
  }

  onMonitorTagItemChanged(
    mustBeInCollection: boolean,
    monitorTag: TagViewModel,
    user?: MonitorTagByUser
  ): void {
    this.ngForm.form.markAsDirty();

    if (mustBeInCollection) {
      if (!this.userSharedMonitorTagsIds.includes(monitorTag.Id)) {
        this.userSharedMonitorTagsIds.push(monitorTag.Id);
      }
    } else {
      _.remove(this.userSharedMonitorTagsIds, (x: number) => x === monitorTag.Id);
    }
  }

  onSelectAllPlaylistsChanged(checked: boolean, user: PlaylistsByUser): void {
    user.OwnedPlaylists.forEach(p =>
      this.onPlaylistItemChanged(checked, p, user)
    );
  }

  onSelectAllTagsChanged(checked: boolean, user: MonitorTagByUser): void {
    user.OwnedMonitorTags.forEach(p =>
      this.onMonitorTagItemChanged(checked, p, user)
    );
  }

  isAllPlaylistsChecked(user: PlaylistsByUser): boolean {
    return _.every(user.OwnedPlaylists, (x: PlaylistDTO) =>
      _.includes(this.userSharedPlaylistsIds, x.Id)
    );
  }

  isAllMonitorTagsCheched(user: MonitorTagByUser): boolean {
    return _.every(user.OwnedMonitorTags, (x: PlaylistDTO) =>
      _.includes(this.userSharedMonitorTagsIds, x.Id)
    );
  }

  isPlaylistSelected(playlist: PlaylistDTO): boolean {
    return _.includes(this.userSharedPlaylistsIds, playlist.Id);
  }

  isMonitorTagSelected(monitorTag: TagViewModel): boolean {
    return _.includes(this.userSharedMonitorTagsIds, monitorTag.Id);
  }

  isLocationSelected(location: Machine) {
    return _.some(
      this.availableMachines,
      machine => machine == location.PhysicalAddress
    );
  }

  isCategorySelected(category: MachineCategory): boolean {
    const activeMachines = category.Machines.filter(m => m.isActive);

    return activeMachines.length
      ? activeMachines.every(m => this.availableMachines.includes(m.PhysicalAddress))
      : this.availableCategoriesIds.includes(category.Id);
  }

  isAllLocationsSelected(): boolean {
    this.filteredCategories = this.categoryFilterPipe.transform(this.categories, this.filterText, this.filterCategoryId);

    return !!this.filteredCategories.length &&
      this.filteredCategories.every(category => {
        const activeMachines = category.Machines.filter(m => m.isActive);

        return !!activeMachines.length
          ? activeMachines.every(machine => this.availableMachines.includes(machine.PhysicalAddress))
          : this.availableCategoriesIds.includes(category.Id)
      });
  }

  isScreenSelected(screen: Monitor) {
    return _.some(this.availableScreenKeys, (x: MonitorKey) => {
      return (
        x.MachinePhysicalAddress === screen.MachinePhysicalAddress &&
        x.MonitorHandler === screen.MonitorHandler
      );
    });
  }

  save() {
    if (this.user.Roles.includes(this.adminRole)) {
      this.user.HasAccessToDisconnectAgents = true;
    }

    this.saveInProgress = true;
    this.updateSharedUserPlaylists();

    if (this.user.Id) {
      const updateModel = new UpdateAccountViewModel(
        this.removeUselessProps(this.user)
      );
      updateModel.AvailableCategories = this.availableCategoriesIds;
      updateModel.AvailableMachines = this.availableMachines;
      const updateMonitors = [...(this.availableScreenKeys ? this.availableScreenKeys : [])];
      if (this.availableMachines && this.availableMachines.length) {
        this.availableMachines.forEach(machine => {
          _.remove(updateMonitors, (x: MonitorKey) => {
            return x.MachinePhysicalAddress === machine;
          });
        });
      }
      updateModel.AvailableScreenKeys = updateMonitors;
      updateModel.SharedPlaylistsIds = this.userSharedPlaylistsIds;
      updateModel.SharedMonitorTagsIds = this.userSharedMonitorTagsIds;

      this.subscriptions.add(
        this.adminService.updateUser(updateModel).pipe(
          finalize(() => this.saveInProgress = false)
        ).subscribe(() => {
          this.toastrService.success(
            `User ${this.user.FirstName} ${this.user.LastName} successfully updated`,
            'Profile update'
          );
          this.close();
        })
      );
    } else {
      const regModel = new RegisterAccountViewModel(this.user);
      regModel.AvailableScreenKeys = this.availableScreenKeys;
      regModel.AvailableMachines = this.availableMachines;
      regModel.AvailableCategories = this.availableCategoriesIds;
      regModel.SharedPlaylistsIds = this.userSharedPlaylistsIds;
      regModel.SharedMonitorTagsIds = this.userSharedMonitorTagsIds;
      regModel.Password = this.passwordModel;
      regModel.ConfirmPassword = this.confirmPasswordModel;

      this.subscriptions.add(
        this.adminService.registerUser(regModel).pipe(
          finalize(() => this.saveInProgress = false)
        ).subscribe(() => {
          this.toastrService.success(
            `User ${this.user.FirstName} ${this.user.LastName} successfully registered`,
            'User registration'
          );
          this.close();
        })
      );
    }
  }

  changePassword() {
    this.authService
      .changePassword({ ...this.changePasswordModel, UserId: this.user.Id })
      .pipe(take(1))
      .subscribe(
        () => {
          this.toastrService.success(
            AppConstants.CHANGING_PASSWORD_SUCCESS,
            AppConstants.CHANGING_PASSWORD_TITLE
          );
          this.changePasswordForm.resetForm();
        },
        () =>
          this.toastrService.error(
            AppConstants.CHANGING_PASSWORD_ERROR,
            AppConstants.CHANGING_PASSWORD_TITLE
          )
      );
  }

  toggleTwoFatorAuth() {
    if (this.user.TwoFactorEnabled) {
      this.deleteDialogService.show(ToastrMessageEnum.TwoFactorAuthDisablingMessage, false)
        .pipe(
          filter((isToContinue: boolean) => !!isToContinue),
          mergeMap(() => this.authService.setTwoFactorEnabled(this.user.Email, false)),
          take(1)
        )
        .subscribe(() => {
          this.user.TwoFactorEnabled = false;
          this.toastrService.success(
            ToastrMessageEnum.TwoFactorAuthDisabledMessage,
            ToastrMessageEnum.ProfileUpdateTitle
          );
      });
    }
    else {
      this.authService.generateTwoFactorAuthenticationCode(this.user.Email)
        .pipe(take(1))
        .subscribe((data) => {
          const dialogRef = this.dialog.open(ModalTwoFactorAuthenticationComponent, {
            width: AppConstants.MODAL_WINDOW_WIDTH,
            disableClose: true,
            panelClass: 'custom-mat-dialog-class',
            restoreFocus: false,
            autoFocus: false
          });
          dialogRef.componentInstance.user = this.user;
          dialogRef.componentInstance.validationData = data;
        });
    }
  }

  createUserApiToken() {
    const modal = this.dialog.open(ModalAddUserApiTokenComponent, {
      width: AppConstants.API_TOKEN_MODAL_WINDOW_WIDTH,
      disableClose: true,
      restoreFocus: false,
      autoFocus: false
    });

    modal.componentInstance.userId = this.user.Id;

    modal.afterClosed()
      .pipe(
        filter((data: ApiToken) => !!data),
        take(1)
      )
      .subscribe(token => {
        this.userApiTokens.push(token);
        this.dataSource = new MatTableDataSource(this.userApiTokens);
      });
  }

  editConsentContent() {
    let legalConsentDialog: MatDialogRef<ModalLegalConsentComponent, any>;

    this.authService.getLegalConsent().pipe(
      tap(data => {
        legalConsentDialog = this.dialog.open(ModalLegalConsentComponent, AppConstants.LEGAL_CONSENT_DIALOG_WINDOW_CONFIG);
        legalConsentDialog.componentInstance.legalConsent = data
          ? new LegalConsentModel(data.Title, data.Text.replace(AppConstants.NEW_LINE_IN_STRING_REGULAR, AppConstants.NEW_LINE_CHARACTER), data.Id)
          : new LegalConsentModel();
        legalConsentDialog.componentInstance.isEditing = true;
      }),
      mergeMap(() => {
        return legalConsentDialog.afterClosed().pipe(
          filter((data: LegalConsentModel) => !!data.Text),
          mergeMap(data => {
            this.isLegalConsentOptionAllowed = true;
            data.Text = data.Text.replace(AppConstants.NEW_LINE_REGULAR, AppConstants.NEW_LINE_IN_STRING_CHARACTER);
            return this.authService.setLegalConsent(data);
          })
        )
      }),
      take(1),
    ).subscribe();
  }

  revokeToken(id: number) {
    this.deleteDialogService.show()
      .pipe(
        filter(isToContinue => !!isToContinue),
        mergeMap(() => this.authService.revokeUserApiToken(id)),
        take(1)
      )
      .subscribe(() => {
        this.userApiTokens = this.userApiTokens.filter(token => token.Id !== id);
        this.dataSource = new MatTableDataSource(this.userApiTokens);

        this.toastrService.success(
          ToastrMessageEnum.ApiTokenRevocationMessage,
          ToastrMessageEnum.ApiTokenMessage
        );
      });
  }

  public trackByIndex(index: number) {
    return index;
  }

  public checkIsAdminRoleChanged(): boolean {
    return this.currentUserRoles.includes(this.adminRole) && !this.user.Roles.includes(this.adminRole);
  }

  private fetchDataByUser(): void {
    this.subscriptions.add(
      this.utilsService
        .getLanguages()
        .subscribe(data => (this.languages = data))
    );

    this.subscriptions.add(
      this.utilsService.getAllRoles().subscribe(data =>
        this.roles = _.orderBy(data, (role: Role) => role.Name, AppConstants.ASC_SORT_DIRECTION))
    );

    this.getCategories();

    if (this.isAdmin) {
      this.subscriptions.add(
        this.admMonitorTagsDataService
          .getMonitorTagsByUsers()
          .subscribe(data => (this.tagsByUsersCollection = data))
      );

      this.subscriptions.add(
        this.admPlaylistsDataService
          .getAllPlaylistByUsers()
          .subscribe(data => (this.playlistsByUsersCollection = data))
      );
    }
    else {
      this.subscriptions.add(
        this.admMonitorTagsDataService
          .getMonitorTagsByCurrentUser()
          .subscribe(data => (this.tagsByUsersCollection = data))
      );

      this.subscriptions.add(
        this.admPlaylistsDataService
          .getAllPlaylists()
          .subscribe(data => (this.playlistsByUsersCollection = data))
      );
    }
  }

  private removeUselessProps(
    model: DisplayAccountViewModel
  ): Partial<DisplayAccountViewModel> {
    const {
      EmailConfirmed,
      AccountStatus,
      AvailableMonitors,
      SharedPlaylists,
      CompanyId,
      Company,
      DateRegistered,
      ...user
    } = model;

    return user;
  }

  private selectAllLocations(categoriesToSelect: MachineCategory[] = this.categories): void {
    categoriesToSelect.forEach(category => {
      if (category.Id) {
        this.availableCategoriesIds.push(category.Id);
      }

      category.Machines.forEach(location => {
        this.availableMachines.push(location.PhysicalAddress);
        location.Monitors?.forEach(monitor => {
          this.availableScreenKeys.push(
            new MonitorKey(monitor.MonitorHandler, monitor.MachinePhysicalAddress)
          );
        });
      })
    });
  }

  private getCategories() {
    this.categories$ = this.adminService.getAllMachines().pipe(
      tap(machines => this.machines = machines),
      mergeMap(machines => combineLatest([
        of(machines),
        this.categoryService.getAllCategories(),
        this.user.Id ? this.monitorsService.getAvailableUserMonitors(this.user.Id) : of(null)
      ])),
      map(([machines, categories, availableMonitors]) => {
        if (machines.some(m => !m.CategoryId)) {
          const uncategorizedCategory = new MachineCategory(
            AppConstants.MACHINE_UNCATEGORIZED_NAME,
            AppConstants.MACHINE_UNCATEGORIZED_BACKGROUND_COLOR
          );

          uncategorizedCategory.Id = null;
          uncategorizedCategory.Machines = machines.filter(location => !location.CategoryId);
          categories.push(uncategorizedCategory);
        }

        categories.forEach(category =>
          category.Machines.forEach(categoryMachine => {
            const machine = machines.find(machine => machine.PhysicalAddress === categoryMachine.PhysicalAddress);

            if (machine) {
              Object.assign(categoryMachine, machine, { isActive : true });
            }
          })
        );

        categories.sort((a, b) => a.Machines.length === 0 && b.Machines.length !== 0 ? 1 :
          a.Machines.length !== 0 && b.Machines.length === 0 ? -1 : 0);

        if (availableMonitors) {
          this.updateAvailableMonitors(availableMonitors);
          this.updateAvailableMachines(machines);
        }

        return categories;
      }),
      tap(categories => {
        this.categories = categories;

        if (this.user.Roles.includes(this.adminRole)) {
          this.selectAllLocations();
        }
      })
    );
  }

  private updateSharedUserPlaylists() {
    this.user.UserIdsSharedAllPlaylists = [];

    this.playlistsByUsersCollection
      .filter(playlistUser => playlistUser.User.Id !== this.user.Id && playlistUser.OwnedPlaylists?.length)
      .forEach(playlistUser => {
        if (this.isAllPlaylistsChecked(playlistUser)) {
          this.user.UserIdsSharedAllPlaylists.push(playlistUser.User.Id);
        }
      });
  }

  private updateAvailableMonitors(availableMonitors: Monitor[]) {
    this.availableScreenKeys = availableMonitors.map(monitor =>
      new MonitorKey(monitor.MonitorHandler, monitor.MachinePhysicalAddress)
    );
  }

  private updateAvailableMachines(machines: Machine[]) {
    this.availableMachines = machines.filter(machine =>
      machine.Monitors.every(monitor =>
        this.availableScreenKeys.some(screenKey =>
          screenKey.MachinePhysicalAddress === monitor.MachinePhysicalAddress
            && screenKey.MonitorHandler === monitor.MonitorHandler
        )
      )
    ).map(machine => machine.PhysicalAddress);
  }
}
